import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import LojaService from '../services/Loja/LojaService';

const LojaCartContext = createContext();

export function LojaCartProvider({ children }) {
  // Inicialização com dados salvos em localStorage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('urb_cart_items');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem('urb_favorites');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const saved = localStorage.getItem('urb_coupon');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  const [selectedShipping, setSelectedShipping] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Persistência automática
  useEffect(() => {
    try {
      localStorage.setItem('urb_cart_items', JSON.stringify(cartItems));
    } catch (e) {}
  }, [cartItems]);

  useEffect(() => {
    try {
      localStorage.setItem('urb_favorites', JSON.stringify(favorites));
    } catch (e) {}
  }, [favorites]);

  useEffect(() => {
    try {
      if (appliedCoupon) {
        localStorage.setItem('urb_coupon', JSON.stringify(appliedCoupon));
      } else {
        localStorage.removeItem('urb_coupon');
      }
    } catch (e) {}
  }, [appliedCoupon]);

  // Cálculos reativos
  const subtotal = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + (Number(item.price) * Number(item.quantity)), 0);
  }, [cartItems]);

  const discount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.type === 'percentage') {
      return Number(((subtotal * appliedCoupon.value) / 100).toFixed(2));
    }
    if (appliedCoupon.type === 'fixed') {
      return Math.min(appliedCoupon.value, subtotal);
    }
    return 0;
  }, [appliedCoupon, subtotal]);

  const shippingCost = useMemo(() => {
    if (!selectedShipping) return 0;
    if (appliedCoupon && appliedCoupon.type === 'free_shipping') return 0;
    return Number(selectedShipping.cost || 0);
  }, [selectedShipping, appliedCoupon]);

  const total = useMemo(() => {
    const rawTotal = subtotal - discount + shippingCost;
    return Math.max(0, Number(rawTotal.toFixed(2)));
  }, [subtotal, discount, shippingCost]);

  const totalItemsCount = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + item.quantity, 0);
  }, [cartItems]);

  // Ações do Carrinho
  const addToCart = (product, variant, quantity = 1) => {
    if (!product || !variant) return;

    setCartItems(prev => {
      const itemKey = `${product.id}_${variant.sku || variant.size + variant.color}`;
      const existingIndex = prev.findIndex(item => item.itemKey === itemKey);

      if (existingIndex >= 0) {
        const next = [...prev];
        const newQty = next[existingIndex].quantity + quantity;
        // Valida se excede estoque
        const maxStock = variant.stock || 99;
        next[existingIndex].quantity = Math.min(newQty, maxStock);
        return next;
      }

      const primaryImg = (product.images && product.images.length > 0) 
        ? (product.images.find(i => i.is_primary)?.url || product.images[0].url || product.images[0])
        : '/images/produtos/camisa-urb.png';

      const unitPrice = product.promotional_price || product.price;

      const newItem = {
        itemKey,
        productId: product.id,
        productName: product.name,
        slug: product.slug,
        sku: variant.sku || product.sku,
        size: variant.size,
        color: variant.color,
        gender: variant.gender || product.gender,
        price: unitPrice,
        originalPrice: product.price,
        image: primaryImg,
        maxStock: variant.stock || 99,
        quantity: Math.min(quantity, variant.stock || 99)
      };

      return [...prev, newItem];
    });

    setIsCartOpen(true);
  };

  const updateQuantity = (itemKey, delta) => {
    setCartItems(prev => {
      return prev.map(item => {
        if (item.itemKey === itemKey) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return {
            ...item,
            quantity: Math.min(newQty, item.maxStock)
          };
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (itemKey) => {
    setCartItems(prev => prev.filter(item => item.itemKey !== itemKey));
  };

  const clearCart = () => {
    setCartItems([]);
    setAppliedCoupon(null);
    setSelectedShipping(null);
    localStorage.removeItem('urb_cart_items');
    localStorage.removeItem('urb_coupon');
  };

  // Cupons
  const applyCoupon = async (code) => {
    setCouponError('');
    if (!code || !code.trim()) {
      setCouponError('Digite um código de cupom.');
      return false;
    }

    const res = await LojaService.validateCoupon(code, subtotal);
    if (res.valid && res.coupon) {
      setAppliedCoupon(res.coupon);
      setCouponError('');
      return true;
    } else {
      setCouponError(res.error || 'Cupom inválido.');
      return false;
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  // Favoritos
  const toggleFavorite = (productId) => {
    setFavorites(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const isFavorite = (productId) => {
    return favorites.includes(productId);
  };

  const value = {
    cartItems,
    subtotal,
    discount,
    shippingCost,
    total,
    totalItemsCount,
    appliedCoupon,
    couponError,
    selectedShipping,
    setSelectedShipping,
    isCartOpen,
    openCart: () => setIsCartOpen(true),
    closeCart: () => setIsCartOpen(false),
    isFavoritesOpen,
    openFavorites: () => setIsFavoritesOpen(true),
    closeFavorites: () => setIsFavoritesOpen(false),
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    applyCoupon,
    removeCoupon,
    favorites,
    toggleFavorite,
    isFavorite
  };

  return (
    <LojaCartContext.Provider value={value}>
      {children}
    </LojaCartContext.Provider>
  );
}

export function useLojaCart() {
  const context = useContext(LojaCartContext);
  if (!context) {
    throw new Error('useLojaCart deve ser utilizado dentro de um LojaCartProvider');
  }
  return context;
}
